import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  FileText,
  TrendingUp,
  Activity,
  Megaphone,
  Eye,
  MousePointerClick,
  User,
  MoreHorizontal,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import advertisementApi from '../../services/advertisementApi';

const StatCard = ({ title, value, icon: Icon, glowColor, valueColor = 'text-white', delay = 0, locale = 'tr' }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ y: -2 }}
    className="group relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#161f31] p-4 transition-all duration-300 hover:border-white/[0.12]"
    style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.02), 0 8px 28px ${glowColor}12` }}
  >
    <div
      className="absolute -right-5 -top-5 h-20 w-20 rounded-full blur-2xl opacity-50 transition-opacity group-hover:opacity-80"
      style={{ backgroundColor: `${glowColor}20` }}
    />
    <div className="relative flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-[#7a96b0]">{title}</p>
        <p className={`mt-1.5 text-2xl font-bold tabular-nums leading-none ${valueColor}`}>
          {typeof value === 'number' ? value.toLocaleString(locale) : value}
        </p>
      </div>
      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border"
        style={{
          backgroundColor: `${glowColor}14`,
          borderColor: `${glowColor}28`,
          boxShadow: `0 0 16px ${glowColor}18`,
        }}
      >
        <Icon className="h-4 w-4" style={{ color: glowColor }} />
      </div>
    </div>
  </motion.div>
);

const DistributionBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#8aaccc]">{label}</span>
        <span className="text-sm font-semibold tabular-nums text-white">{value}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const AdStatRow = ({ label, value, dotColor, locale = 'tr' }) => (
  <div className="flex items-center justify-between py-1">
    <div className="flex items-center gap-2.5">
      <span
        className="h-2 w-2 flex-shrink-0 rounded-full"
        style={{ backgroundColor: dotColor, boxShadow: `0 0 8px ${dotColor}80` }}
      />
      <span className="text-sm text-[#8aaccc]">{label}</span>
    </div>
    <span className="text-sm font-semibold tabular-nums text-white">
      {typeof value === 'number' ? value.toLocaleString(locale) : value}
    </span>
  </div>
);

const ChartPlaceholder = ({ impressions, clicks, noDataLabel }) => {
  const hasData = impressions > 0 || clicks > 0;
  const points = [0.15, 0.35, 0.25, 0.55, 0.45, 0.7, 0.6, 0.85, 0.75, 0.9];

  return (
    <div className="relative mt-4 h-36 overflow-hidden rounded-xl border border-white/[0.06] bg-[#0d1424]/60">
      {/* Grid lines */}
      <div className="absolute inset-0 flex flex-col justify-between py-3 px-1 pointer-events-none">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-px w-full bg-white/[0.04]" />
        ))}
      </div>

      {hasData ? (
        <svg viewBox="0 0 300 120" className="absolute inset-0 h-full w-full p-3" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3d8ef0" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#3d8ef0" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon
            fill="url(#chartGrad)"
            points={`0,120 ${points.map((p, i) => `${(i / (points.length - 1)) * 300},${120 - p * 100}`).join(' ')} 300,120`}
          />
          <polyline
            fill="none"
            stroke="#3d8ef0"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={points.map((p, i) => `${(i / (points.length - 1)) * 300},${120 - p * 100}`).join(' ')}
          />
        </svg>
      ) : (
        <div className="flex h-full items-center justify-center">
          <p className="text-xs text-[#4d6a8a]">{noDataLabel}</p>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { t, i18n } = useTranslation('admin');
  const { authToken } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    freeUsers: 0,
    plusUsers: 0,
    adminUsers: 0,
    totalNotes: 0,
    totalAdvertisements: 0,
    activeAdvertisements: 0,
    totalImpressions: 0,
    totalClicks: 0,
  });
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const usersResult = await adminApi.getUsers({ token: authToken });
      if (usersResult.success) {
        const users = usersResult.users || [];
        const activeUsers = users.filter((u) => u.status === 'active');
        const freeUsers = users.filter((u) => {
          const role = u.role || 'free';
          return role === 'free' || role === 'user' || !u.role;
        });
        const plusUsers = users.filter((u) => u.role === 'plus');
        const adminUsers = users.filter((u) => u.role === 'admin');
        const totalNotes = users.reduce((sum, user) => sum + (user.noteCount || 0), 0);

        let totalAdvertisements = 0;
        let activeAdvertisements = 0;
        let totalImpressions = 0;
        let totalClicks = 0;

        try {
          const adsResult = await advertisementApi.getAdvertisements({ token: authToken });
          if (adsResult.success) {
            const ads = adsResult.advertisements || [];
            totalAdvertisements = ads.length;
            activeAdvertisements = ads.filter((ad) => ad.isActive).length;
            totalImpressions = ads.reduce((sum, ad) => sum + (ad.currentImpressions || 0), 0);
            totalClicks = ads.reduce((sum, ad) => sum + (ad.currentClicks || 0), 0);
          }
        } catch (error) {
          console.error('Load advertisements error:', error);
        }

        setStats({
          totalUsers: users.length,
          activeUsers: activeUsers.length,
          freeUsers: freeUsers.length,
          plusUsers: plusUsers.length,
          adminUsers: adminUsers.length,
          totalNotes,
          totalAdvertisements,
          activeAdvertisements,
          totalImpressions,
          totalClicks,
        });
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const statCards = [
    { title: t('adminDashboard.statCards.totalUsers'), value: stats.totalUsers, icon: Users, glowColor: '#3d8ef0' },
    { title: t('adminDashboard.statCards.activeUsers'), value: stats.activeUsers, icon: Activity, glowColor: '#10b981', valueColor: 'text-emerald-400' },
    { title: t('adminDashboard.statCards.freeUsers'), value: stats.freeUsers, icon: User, glowColor: '#64748b', valueColor: 'text-[#c8d8eb]' },
    { title: t('adminDashboard.statCards.plusUsers'), value: stats.plusUsers, icon: TrendingUp, glowColor: '#f59e0b', valueColor: 'text-amber-400' },
    { title: t('adminDashboard.statCards.totalNotes'), value: stats.totalNotes, icon: FileText, glowColor: '#a855f7', valueColor: 'text-purple-400' },
    { title: t('adminDashboard.statCards.activeAds'), value: stats.activeAdvertisements, icon: Megaphone, glowColor: '#f97316', valueColor: 'text-orange-400' },
    { title: t('adminDashboard.statCards.totalImpressions'), value: stats.totalImpressions, icon: Eye, glowColor: '#6366f1' },
    { title: t('adminDashboard.statCards.totalClicks'), value: stats.totalClicks, icon: MousePointerClick, glowColor: '#ec4899', valueColor: 'text-pink-400' },
  ];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-3 text-[#7a96b0]">
          <Loader2 className="h-5 w-5 animate-spin text-[#3d8ef0]" />
          <span>{t('adminDashboard.dashboardLoading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">{t('adminDashboard.title')}</h2>
        <p className="mt-1 text-sm text-[#7a96b0]">{t('adminDashboard.subtitle')}</p>
      </div>

      {/* 8 stat cards — 2 rows × 4 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card, index) => (
          <StatCard key={card.title} {...card} delay={index * 0.04} locale={i18n.language} />
        ))}
      </div>

      {/* Bottom panels */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Kullanıcı Dağılımı */}
        <div className="rounded-xl border border-white/[0.06] bg-[#161f31] p-5">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="text-base font-semibold text-white">{t('adminDashboard.userDistribution')}</h3>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
                className="rounded-lg p-1.5 text-[#4d6a8a] transition-colors hover:bg-white/[0.05] hover:text-white"
                aria-label={t('adminDashboard.menu')}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full z-10 mt-1 min-w-[140px] overflow-hidden rounded-xl border border-white/[0.08] bg-[#0d1424] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.5)]">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      loadDashboardData();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#c8d8eb] hover:bg-white/[0.05]"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    {t('adminDashboard.refresh')}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-5">
            <DistributionBar
              label={t('adminDashboard.statCards.freeUsers')}
              value={stats.freeUsers}
              total={stats.totalUsers}
              color="#64748b"
            />
            <DistributionBar
              label="Plus"
              value={stats.plusUsers}
              total={stats.totalUsers}
              color="#f59e0b"
            />
            <DistributionBar
              label={t('adminDashboard.adminLabel')}
              value={stats.adminUsers}
              total={stats.totalUsers}
              color="#f87171"
            />
          </div>
        </div>

        {/* Reklam İstatistikleri */}
        <div className="rounded-xl border border-white/[0.06] bg-[#161f31] p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-white">{t('adminDashboard.adStats')}</h3>
            <span className="rounded-md border border-white/[0.08] bg-[#0d1424] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#7a96b0]">
              {t('adminDashboard.last30Days')}
            </span>
          </div>
          <div className="space-y-1">
            <AdStatRow label={t('adminDashboard.totalAds')} value={stats.totalAdvertisements} dotColor="#3d8ef0" locale={i18n.language} />
            <AdStatRow label={t('adminDashboard.statCards.activeAds')} value={stats.activeAdvertisements} dotColor="#10b981" locale={i18n.language} />
            <AdStatRow label={t('adminDashboard.statCards.totalImpressions')} value={stats.totalImpressions} dotColor="#6366f1" locale={i18n.language} />
            <AdStatRow label={t('adminDashboard.statCards.totalClicks')} value={stats.totalClicks} dotColor="#ec4899" locale={i18n.language} />
          </div>
          <ChartPlaceholder impressions={stats.totalImpressions} clicks={stats.totalClicks} noDataLabel={t('adminDashboard.noChartData')} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
